import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, Clock, Award, CheckCircle2, Play } from "lucide-react";
import { useAuth } from 'contexts/AuthContext';
import { toast } from "sonner";

/* ─── COLORES DE MARCA ────────────────────────────────────────────────────────
   primary   → --brand-primary
   secondary → --brand-secondary
   accent    → --brand-accent
──────────────────────────────────────────────────────────────────────────── */
const P = (o = 1) => `rgb(var(--brand-primary-rgb, 0 255 255) / ${o})`;
const S = (o = 1) => `rgb(var(--brand-secondary-rgb, 0 255 0) / ${o})`;
const A = (o = 1) => `rgb(var(--brand-accent-rgb, 255 255 0) / ${o})`;

const Styles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

    .cyber-font { font-family: 'Inter', sans-serif; }

    /* Dentro de las tarjetas el texto es siempre claro (fondo oscuro) */
    .mm-root .glass-card .t-main  { color: #fff; }
    .mm-root .glass-card .t-dim   { color: rgb(255 255 255 / .75); }
    .mm-root .glass-card .t-faint { color: rgb(255 255 255 / .55); }

    /* Tarjetas oscuras: sube/baja los dos 0.6 para más o menos oscuridad */
    .glass-card {
      background:
        linear-gradient(rgb(0 0 0 / 0.6), rgb(0 0 0 / 0.6)),
        rgb(var(--brand-bg-rgb, 6 5 20) / 0.9);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border: 1px solid rgb(var(--brand-text-rgb, 255 255 255) / 0.1);
      border-radius: 16px;
      transition: all 0.3s ease;
    }

    /* Neones más fuertes */
    .neon-glow-cyan {
      box-shadow:
        0 0 18px rgb(var(--brand-primary-rgb, 0 255 255) / 0.55),
        0 0 40px rgb(var(--brand-primary-rgb, 0 255 255) / 0.22),
        inset 0 0 14px rgb(var(--brand-primary-rgb, 0 255 255) / 0.08);
      border-color: rgb(var(--brand-primary-rgb, 0 255 255) / 0.85);
    }
    .neon-glow-yellow {
      box-shadow:
        0 0 18px rgb(var(--brand-accent-rgb, 255 255 0) / 0.55),
        0 0 40px rgb(var(--brand-accent-rgb, 255 255 0) / 0.22),
        inset 0 0 14px rgb(var(--brand-accent-rgb, 255 255 0) / 0.08);
      border-color: rgb(var(--brand-accent-rgb, 255 255 0) / 0.85);
    }
    .neon-glow-green {
      box-shadow:
        0 0 18px rgb(var(--brand-secondary-rgb, 0 255 0) / 0.55),
        0 0 40px rgb(var(--brand-secondary-rgb, 0 255 0) / 0.22),
        inset 0 0 14px rgb(var(--brand-secondary-rgb, 0 255 0) / 0.08);
      border-color: rgb(var(--brand-secondary-rgb, 0 255 0) / 0.85);
    }

    /* Botón COMENZAR */
    .neon-btn-green {
      background-color: var(--brand-secondary, #fd853f);
      color: white;
      box-shadow:
        0 0 12px rgb(var(--brand-secondary-rgb, 253 133 63) / 0.7),
        0 0 26px rgb(var(--brand-secondary-rgb, 253 133 63) / 0.4);
      border: 1px solid var(--brand-secondary, #ff963a);
      transition: all 0.3s ease;
    }
    .neon-btn-green:hover {
      filter: brightness(1.15);
      box-shadow:
        0 0 18px rgb(var(--brand-secondary-rgb, 253 133 63) / 0.9),
        0 0 36px rgb(var(--brand-secondary-rgb, 253 133 63) / 0.55);
    }

    /* Pestañas de filtro */
    .filter-tab {
      padding: 8px 24px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.05em;
      transition: all 0.2s;
      cursor: pointer;
      color: #fff;
      background: rgb(var(--brand-primary-rgb, 0 255 255) / 0.16);
      border: 1px solid rgb(var(--brand-primary-rgb, 0 255 255) / 0.45);
    }
    .filter-tab:hover {
      background: rgb(var(--brand-primary-rgb, 0 255 255) / 0.3);
      border-color: rgb(var(--brand-primary-rgb, 0 255 255) / 0.8);
      box-shadow: 0 0 14px rgb(var(--brand-primary-rgb, 0 255 255) / 0.45);
    }
    .filter-tab.active {
      background: var(--brand-primary, #00FFFF);
      border-color: var(--brand-primary, #00FFFF);
      color: #000;
      box-shadow: 0 0 18px rgb(var(--brand-primary-rgb, 0 255 255) / 0.7);
    }

    .badge-cyber {
      font-size: 10px;
      font-weight: 700;
      padding: 2px 10px;
      border-radius: 6px;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      border: 1px solid transparent;
    }

    .title-neon {
      color: var(--brand-text, #FFF);
      text-shadow: 0 0 10px rgb(var(--brand-primary-rgb, 255 255 255) / 0.35);
    }
  `}</style>
);

/* ─── FUNCIÓN AUXILIAR PARA EXTRAER EL NÚMERO DE UNIDAD ─── */
const getUnitNumber = (title) => {
  if (!title) return 999;
  const match = title.match(/UNIDAD\s+(\d+)/i);
  return match ? parseInt(match[1], 10) : 999;
};

const FILTERS = [
  ["all", "Todos"],
  ["pending", "Pendientes"],
  ["completed", "Completados"],
];

const MyModules = () => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const { token } = useAuth();
  const navigate = useNavigate();

  const fetchAssignedModules = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/modules/assigned`, {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) throw new Error(`Failed to fetch modules: ${response.status}`);
      const data = await response.json();
      setModules(data);
    } catch (error) {
      console.error("Error fetching modules:", error);
      toast.error("Error al cargar módulos asignados");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAssignedModules();
  }, [fetchAssignedModules]);

  const filteredModules = modules
    .filter((module) => {
      if (filter === "pending") return module.assignment_status === "pending";
      if (filter === "completed") return module.assignment_status === "completed";
      return true;
    })
    .sort((a, b) => getUnitNumber(a.title) - getUnitNumber(b.title));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div
          className="w-12 h-12 border-4 rounded-full animate-spin"
          style={{
            borderColor: 'var(--brand-primary, #00FFFF)',
            borderTopColor: 'transparent',
            boxShadow: `0 0 15px ${P(1)}`,
          }}
        />
      </div>
    );
  }

  return (
    <div className="cyber-font mm-root min-h-screen p-8 relative">
      <Styles />

      {/* HEADER */}
      <div className="mb-10">
        <h1
          className="text-4xl font-black t-main tracking-tight title-neon"
          style={{ fontFamily: 'Roberta' }}
        >
          Mis Módulos Asignados
        </h1>
        <p className="t-dim text-sm mt-2 font-medium">
          Completa los módulos asignados para desarrollar tus habilidades
        </p>
      </div>

      {/* STATS CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        <div className="glass-card p-6 neon-glow-cyan flex items-center gap-5">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ background: P(0.18), border: `1px solid ${P(0.5)}` }}>
            <BookOpen className="w-6 h-6 t-primary" />
          </div>
          <div>
            <p className="text-[10px] font-black t-faint tracking-widest uppercase">Total Módulos</p>
            <p className="text-3xl font-black t-main">{modules.length}</p>
          </div>
        </div>

        <div className="glass-card p-6 neon-glow-yellow flex items-center gap-5">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ background: A(0.18), border: `1px solid ${A(0.5)}` }}>
            <Clock className="w-6 h-6 t-accent" />
          </div>
          <div>
            <p className="text-[10px] font-black t-faint tracking-widest uppercase">Pendientes</p>
            <p className="text-3xl font-black t-main">
              {modules.filter((m) => m.assignment_status === "pending").length}
            </p>
          </div>
        </div>

        <div className="glass-card p-6 neon-glow-green flex items-center gap-5">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ background: S(0.18), border: `1px solid ${S(0.5)}` }}>
            <CheckCircle2 className="w-6 h-6 t-secondary" />
          </div>
          <div>
            <p className="text-[10px] font-black t-faint tracking-widest uppercase">Completados</p>
            <p className="text-3xl font-black t-main">
              {modules.filter((m) => m.assignment_status === "completed").length}
            </p>
          </div>
        </div>
      </div>

      {/* FILTERS */}
      <div className="flex gap-3 mb-8">
        {FILTERS.map(([id, label]) => (
          <button
            key={id}
            onClick={() => setFilter(id)}
            className={`filter-tab ${filter === id ? "active" : ""}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* MODULE LIST */}
      <div className="grid grid-cols-1 gap-6">
        {filteredModules.map((module, index) => {
          const currentUnit = getUnitNumber(module.title);
          const previousUnit = index > 0 ? getUnitNumber(filteredModules[index - 1].title) : null;
          const showSeparator = currentUnit !== 999 && currentUnit !== previousUnit;
          const isCompleted = module.assignment_status === 'completed';

          return (
            <React.Fragment key={module.id}>
              {showSeparator && (
                <div className="mt-6 mb-2 border-l-4 pl-4 flex items-center"
                  style={{ borderColor: 'var(--brand-primary, #00FFFF)' }}>
                  <span className="text-sm font-black t-primary uppercase tracking-[0.2em] title-neon">
                    Unidad {currentUnit}
                  </span>
                </div>
              )}

              <div className="glass-card p-8 neon-glow-cyan group relative overflow-hidden">
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ background: `linear-gradient(90deg, ${P(0.08)}, transparent)` }} />

                <div className="flex items-start justify-between gap-8 relative z-10">
                  <div className="flex-1">
                    {/* Badges */}
                    <div className="flex items-center gap-3 mb-4">
                      <span className="badge-cyber"
                        style={{ background: P(0.15), borderColor: P(0.45), color: '#fff' }}>
                        {module.difficulty === "beginner" ? "Principiante" : module.difficulty === "intermediate" ? "Intermedio" : "Avanzado"}
                      </span>
                      <span className="badge-cyber"
                        style={{ background: S(0.15), borderColor: S(0.45), color: '#fff' }}>
                        {module.category}
                      </span>
                      {isCompleted ? (
                        <span className="badge-cyber"
                          style={{ background: S(0.25), borderColor: S(0.7), color: '#fff' }}>
                          Completado
                        </span>
                      ) : (
                        <span className="badge-cyber"
                          style={{ background: A(0.18), borderColor: A(0.55), color: '#fff' }}>
                          Pendiente
                        </span>
                      )}
                      {module.mandatory && (
                        <span className="badge-cyber border-red-500/40 bg-red-500/15 text-red-300">Obligatorio</span>
                      )}
                    </div>

                    <h3
                      className="text-3xl font-black t-main mb-3 tracking-tight transition-all"
                      style={{ fontFamily: 'Roberta, serif' }}
                    >
                      {module.title}
                    </h3>
                    <p className="t-dim text-sm leading-relaxed mb-6 line-clamp-2 max-w-3xl">
                      {module.description}
                    </p>

                    {/* Info Bar */}
                    <div className="flex flex-wrap gap-6 text-[11px] font-bold t-faint tracking-widest uppercase">
                      <div className="flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 t-primary" />
                        <span>{module.duration_minutes} MIN</span>
                      </div>
                      {module.skills && (
                        <div className="flex items-center gap-2">
                          <Award className="w-3.5 h-3.5 t-primary" />
                          <span>{module.skills.length} HABILIDADES</span>
                        </div>
                      )}
                    </div>

                    {/* Skills Chips */}
                    {module.skills && (
                      <div className="flex flex-wrap gap-2 mt-5">
                        {module.skills.map((skill, idx) => (
                          <span key={idx}
                            className="px-3 py-1 text-[9px] font-black rounded-lg t-faint uppercase tracking-wider"
                            style={{ background: P(0.1), border: `1px solid ${P(0.3)}` }}>
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Botón Comenzar */}
                  <button
                    onClick={() => {
                      if (module.scorm_player) {
                        navigate(`/scorm-player/${module.id}`);
                      } else {
                        navigate(`/module/${module.id}`);
                      }
                    }}
                    className="neon-btn-green px-10 py-4 rounded-xl flex items-center gap-3 text-sm tracking-widest"
                  >
                    <Play className="w-4 h-4 fill-white" />
                    {isCompleted ? "REVISAR" : "COMENZAR"}
                  </button>
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* Estado vacío */}
      {filteredModules.length === 0 && (
        <div className="glass-card p-20 text-center neon-glow-cyan">
          <BookOpen className="w-16 h-16 mx-auto mb-6" style={{ color: P(0.35) }} />
          <h3 className="text-2xl font-black t-main mb-2">No hay módulos disponibles</h3>
          <p className="t-faint text-sm">Prueba a cambiar el filtro o consulta con tu administrador.</p>
        </div>
      )}
    </div>
  );
};

export default MyModules;